import Form from "antd/lib/form";
import Typography from "antd/lib/typography";
import type { UploadFile, UploadProps } from "antd/lib/upload";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SCENARIO_PERIOD_TEMPLATE_LINK } from "@sol/sdk";
import { Card } from "@sol/uikit/data-display/Card/Card";
import { Input } from "@sol/uikit/data-entry/Input/Input";
import { Upload } from "@sol/uikit/data-entry/Upload/Upload";
import { Alert } from "@sol/uikit/feedback/Alert/Alert";
import { Button } from "@sol/uikit/general/Button/Button";

import type { ClassroomTemplateFormValues } from "../forms/ClassroomTemplateForm";

const stubUploadRequest: UploadProps["customRequest"] = ({ onSuccess }) => {
    setTimeout(() => onSuccess?.("ok"), 0);
};

const getFileList = (file?: File | null): UploadFile[] => {
    if (!file) {
        return [];
    }
    return [{ uid: `${file.name}-${file.size}-${file.lastModified}`, name: file.name, status: "done" }];
};

type Props = {
    rank: number;
    actionTitle: string;
    description?: string;
    pedagogicalHours: number;
    formIndex: number;
    started?: boolean;
};

export const ClassroomTrainingActionEditItem = ({
    rank,
    actionTitle,
    description,
    pedagogicalHours,
    formIndex,
    started = false,
}: Props) => {
    const [t] = useTranslation();
    const { control } = useFormContext<ClassroomTemplateFormValues>();
    const Title = Typography.Title;

    return (
        <Card data-cy={`classroom-edit-action-item-${rank}`}>
            <div className="flex flex-col gap-ant-md">
                <div>
                    <Title level={5} className="!mb-0">
                        {actionTitle}
                    </Title>
                    <Typography.Text type="secondary" className="text-xs">
                        {pedagogicalHours}h
                    </Typography.Text>
                    {description ? (
                        <Typography.Paragraph type="secondary" className="mt-ant-xs !mb-0">
                            {description}
                        </Typography.Paragraph>
                    ) : null}
                </div>

                <Controller
                    name={`trainingActions.${formIndex}.rank`}
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={rank} />}
                />

                <Controller
                    name={`trainingActions.${formIndex}.erpIdentifier`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Form.Item
                            label={t("form.classroomTemplateEdit.trainingActions.erpIdentifier.label")}
                            required
                            validateStatus={error ? "error" : ""}
                            help={error?.message}
                            layout="vertical"
                            className="mb-0"
                        >
                            <Input
                                size="middle"
                                placeholder={t("features.classroomCreation.step-2.erpIdentifier.placeholder")}
                                data-cy={`classroom-edit-action-id-${rank}`}
                                {...field}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name={`trainingActions.${formIndex}.erpFile`}
                    control={control}
                    render={({ field: { onBlur, onChange, name, ref, value }, fieldState: { error } }) => (
                        <Form.Item
                            label={t("form.classroomTemplateEdit.trainingActions.erpFile.label")}
                            validateStatus={error ? "error" : ""}
                            help={error?.message}
                            layout="vertical"
                            className="mb-0"
                        >
                            <Typography.Paragraph className="!mb-ant-sm text-color-ant-secondary">
                                {t("features.classroomCreation.step-2.erpFile.description.ypareo")}
                                <br />
                                {t("features.classroomCreation.step-2.erpFile.description.googleSheet")}{" "}
                                <Typography.Link
                                    href={SCENARIO_PERIOD_TEMPLATE_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-cy={`classroom-edit-action-data-template-link-${rank}`}
                                >
                                    {t("features.classroomCreation.step-2.erpFile.description.templateLink")}
                                </Typography.Link>
                            </Typography.Paragraph>
                            {started && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={t("form.classroomTemplateEdit.trainingActions.erpFile.alreadyStarted")}
                                    className="mb-ant-sm"
                                    data-cy={`classroom-edit-action-data-started-${rank}`}
                                />
                            )}
                            <Upload
                                multiple={false}
                                maxCount={1}
                                accept=".csv,.xlsx"
                                disabled={started}
                                fileList={getFileList(value as File | null)}
                                customRequest={stubUploadRequest}
                                onRemove={() => {
                                    onChange(null);
                                    return true;
                                }}
                                onChange={info => {
                                    if (info.file.status === "removed" || info.fileList.length === 0) {
                                        onChange(null);
                                        return;
                                    }
                                    const nextFile = info.file.originFileObj;
                                    if (nextFile && nextFile !== value) {
                                        onChange(nextFile);
                                    }
                                }}
                                data-cy={`classroom-edit-action-data-${rank}`}
                            >
                                <Button
                                    ref={ref}
                                    name={name}
                                    size="middle"
                                    onBlur={onBlur}
                                    disabled={started}
                                    data-cy={`classroom-edit-action-data-trigger-${rank}`}
                                >
                                    {t("form.classroomTemplateEdit.trainingActions.erpFile.reimport")}
                                </Button>
                            </Upload>
                        </Form.Item>
                    )}
                />
            </div>
        </Card>
    );
};
